import { useEffect, useRef, useState } from 'react'
import i18n from '../../../i18n'
import { bruktbilfinansieringAPI } from '../../../services/api'
import type { LoanAmountProfileResponse } from '../../../types/bruktbilfinansiering'
import type { BruktbilfinansieringFilters } from './useBruktbilfinansiering'

const DEBOUNCE_MS = 350

export function useLoanAmountProfile(
  bankOrgNr: string,
  filters: BruktbilfinansieringFilters,
  selectedClasses: string[],
  compareBanks: string[],
) {
  const [data, setData] = useState<LoanAmountProfileResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchKey = JSON.stringify({
    bankOrgNr,
    startDate: filters.startDate,
    endDate: filters.endDate,
    varebil: filters.varebil ?? null,
    dealerPostnummer: filters.dealerPostnummer ?? null,
    buyerPostnummer: filters.buyerPostnummer ?? null,
    classes: [...selectedClasses].sort(),
    compareBanks: [...compareBanks].sort(),
  })

  const isFirstRender = useRef(true)

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      setError(null)
      try {
        const res = await bruktbilfinansieringAPI.getLoanAmountProfile(bankOrgNr, {
          start_date: filters.startDate,
          end_date: filters.endDate,
          classes: selectedClasses.length ? selectedClasses : undefined,
          compare_bank_org_nrs: compareBanks.length ? compareBanks : undefined,
          varebil: filters.varebil ?? undefined,
          dealer_postnummer: filters.dealerPostnummer ?? undefined,
          buyer_postnummer: filters.buyerPostnummer ?? undefined,
        })
        if (!cancelled) setData(res.data)
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : i18n.t('errors.loanAmountProfile', { ns: 'bbf' }))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (isFirstRender.current) {
      isFirstRender.current = false
      run()
      return () => { cancelled = true }
    }
    const t = setTimeout(run, DEBOUNCE_MS)
    return () => { cancelled = true; clearTimeout(t) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey])

  return { data, loading, error }
}
